import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FullscreenModal } from '@/components/global/FullscreenModal/FullscreenModal'
import { Button } from '@/components/priority/Button/Button'
import { Flex } from '@/components/priority/Flex/Flex'
import { Text } from '@/components/priority/Text/Text'

type BleedWarningModalProps = {
  /** Whether the modal is visible */
  show: boolean
  /**
   * Back is waiting for the all-pages margin check: a neutral title, one
   * sentence and Close alone, with no warning icon and no notice, so a
   * product with nothing in the band never shows a warning on its way out.
   */
  checking?: boolean
  /** Callback to close the modal */
  onClose: () => void
  /** Callback when user chooses to continue despite warnings */
  onContinue: () => void
}

/**
 * Modal displayed when user attempts to navigate back and there are items
 * overlapping product bleed/margin areas, and, in its checking state, while
 * Back waits for the check that decides whether there are.
 *
 * Matches the PrintImageQualityModal structure and styling.
 */
export const BleedWarningModal = ({
  show,
  checking = false,
  onClose,
  onContinue,
}: BleedWarningModalProps) => {
  const { t } = useTranslation()
  // A closed modal fades out over its content, so it keeps showing the state
  // it closed in: a check that ends clean closes the checking state without
  // the warning state ever painting.
  const [shownChecking, setShownChecking] = useState(checking)
  if (show && shownChecking !== checking) setShownChecking(checking)

  return (
    <FullscreenModal show={[show, onClose]} hideClose={false}>
      <Flex
        height="300px"
        width="600px"
        column
        center
        gap="32px"
        padding="0 32px"
      >
        <Flex gap="12px" column align="center">
          <Flex gap="12px" align="center">
            {!shownChecking && <Text size="32px">⚠️</Text>}
            <Text
              weight="500"
              size="24px"
              align="center"
              data-testid="bleed-warning-modal-title"
            >
              {shownChecking
                ? t('editor.bleedWarningModal.checkingTitle')
                : t('editor.bleedWarningModal.title')}
            </Text>
          </Flex>
          <Flex gap="12px" column align="center">
            <Text
              width="500px"
              align="center"
              data-testid="bleed-warning-modal-message"
            >
              {shownChecking
                ? t('editor.bleedWarningModal.checkingMessage')
                : t('editor.bleedWarningModal.message')}
            </Text>
            {!shownChecking && (
              <Text width="500px" align="center" weight="500" size="14px">
                {t('editor.bleedWarningModal.notice')}
              </Text>
            )}
          </Flex>
        </Flex>
        <Flex gap="12px">
          {shownChecking ? (
            <Button
              primary
              onClick={onClose}
              aria-label={t(
                'editor.bleedWarningModal.checkingCloseButtonAriaLabel',
              )}
              data-testid="bleed-warning-modal-checking-close"
            >
              {t('editor.bleedWarningModal.closeButton')}
            </Button>
          ) : (
            <>
              <Button
                default
                onClick={onClose}
                aria-label={t('editor.bleedWarningModal.closeButtonAriaLabel')}
                data-testid="bleed-warning-modal-close"
              >
                {t('editor.bleedWarningModal.closeButton')}
              </Button>
              <Button
                primary
                onClick={onContinue}
                aria-label={t(
                  'editor.bleedWarningModal.continueButtonAriaLabel',
                )}
                data-testid="bleed-warning-modal-acknowledge"
              >
                {t('editor.bleedWarningModal.continueButton')}
              </Button>
            </>
          )}
        </Flex>
      </Flex>
    </FullscreenModal>
  )
}
